"use client"
import React from "react"
import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { analyticsAPI } from "../services/api"
import { Box, Flex, Grid, Heading, Text, Select } from "@radix-ui/themes"
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../components/ui/card"
import { CalendarIcon } from "lucide-react"
import { Line, Bar, Doughnut } from "react-chartjs-2"
import PageHeader from "../components/ui/dashboard/PageHeader"
import LoadingSpinner from "../components/ui/dashboard/LoadingSpinner"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js"

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement)

export default function AnalyticsPage() {
  const [period, setPeriod] = useState("7d")

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ["analytics-overview", period],
    queryFn: () => analyticsAPI.getOverview(period),
  })

  const { data: ordersAnalytics, isLoading: ordersLoading } = useQuery({
    queryKey: ["analytics-orders", period],
    queryFn: () => analyticsAPI.getOrdersAnalytics(period),
  })

  const { data: revenueAnalytics, isLoading: revenueLoading } = useQuery({
    queryKey: ["analytics-revenue", period],
    queryFn: () => analyticsAPI.getRevenueAnalytics(period),
  })

  const { data: topRestaurants, isLoading: restaurantsLoading } = useQuery({
    queryKey: ["analytics-top-restaurants", period],
    queryFn: () => analyticsAPI.getTopRestaurants(period),
  })

  const periodOptions = [
    { value: "24h", label: "Last 24 Hours" },
    { value: "7d", label: "Last 7 Days" },
    { value: "30d", label: "Last 30 Days" },
    { value: "90d", label: "Last 90 Days" },
    { value: "1y", label: "Last Year" },
  ]

  const revenueChartData = {
    labels: revenueAnalytics?.data?.labels || [],
    datasets: [
      {
        label: "Revenue",
        data: revenueAnalytics?.data?.data || [],
        borderColor: "rgb(239, 68, 68)",
        backgroundColor: "rgba(239, 68, 68, 0.1)",
        tension: 0.1,
      },
    ],
  }

  const ordersChartData = {
    labels: ordersAnalytics?.data?.labels || [],
    datasets: [
      {
        label: "Orders",
        data: ordersAnalytics?.data?.data || [],
        backgroundColor: "rgba(59, 130, 246, 0.8)",
      },
    ],
  }

  const orderStatusData = {
    labels: ["Delivered", "Cancelled", "In Progress"],
    datasets: [
      {
        data: [
          overview?.data?.ordersByStatus?.delivered || 0,
          overview?.data?.ordersByStatus?.cancelled || 0,
          overview?.data?.ordersByStatus?.inProgress || 0,
        ],
        backgroundColor: ["rgba(34, 197, 94, 0.8)", "rgba(239, 68, 68, 0.8)", "rgba(251, 191, 36, 0.8)"],
        borderWidth: 0,
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top" as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  }

  const doughnutOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "bottom" as const,
      },
    },
  }

  if (overviewLoading || ordersLoading || revenueLoading || restaurantsLoading) {
    return <LoadingSpinner size="3" text="Loading analytics..." />
  }

  return (
    <Flex direction="column" gap="6">
      <PageHeader
        title="Analytics Dashboard"
        action={
          <Flex align="center" gap="2">
            <CalendarIcon size={20} style={{ color: "var(--gray-9)" }} />
            <Select.Root value={period} onValueChange={setPeriod}>
              <Select.Trigger />
              <Select.Content>
                {periodOptions.map((option) => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Flex>
        }
      />

      {/* Overview Cards */}
      <Grid columns={{ initial: "1", md: "2", lg: "4" }} gap="6">
        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                    />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Total Revenue</dt>
                  <dd className="text-2xl font-semibold text-gray-900 dark:text-white">SDG {overview?.data?.totalRevenue?.toLocaleString() || "0"}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                    />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Total Orders</dt>
                  <dd className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {overview?.data?.totalOrders?.toLocaleString() || "0"}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                    />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">New Customers</dt>
                  <dd className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {overview?.data?.newCustomers?.toLocaleString() || "0"}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-orange-500 rounded-md flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Avg Order Value</dt>
                  <dd className="text-2xl font-semibold text-gray-900 dark:text-white">SDG {overview?.data?.avgOrderValue?.toFixed(2) || "0.00"}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </Grid>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Revenue Trend</CardTitle>
            <CardDescription>Revenue over time</CardDescription>
          </CardHeader>
          <CardContent>
            <Line data={revenueChartData} options={chartOptions} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Orders Trend</CardTitle>
            <CardDescription>Number of orders over time</CardDescription>
          </CardHeader>
          <CardContent>
            <Bar data={ordersChartData} options={chartOptions} />
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Order Status Distribution</CardTitle>
            <CardDescription>Breakdown of order statuses</CardDescription>
          </CardHeader>
          <CardContent>
            <Doughnut data={orderStatusData} options={doughnutOptions} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top Restaurants</CardTitle>
            <CardDescription>Best performing restaurants</CardDescription>
          </CardHeader>
          <CardContent>
            {restaurantsLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
              </div>
            ) : (
              <div className="space-y-4">
                {topRestaurants?.data?.map((restaurant: any, index: number) => (
                  <div key={restaurant._id} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-6">#{index + 1}</span>
                      <span className="ml-3 text-sm font-medium text-gray-900 dark:text-white">
                        {restaurant.name}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">SDG {restaurant.revenue?.toLocaleString() || "0"}
                    </div>
                  </div>
                )) || (
                  <div className="text-center text-gray-500 dark:text-gray-400">No data available</div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Flex>
  )
}
